import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import api from '../../services/auth';
import {
  Box,
  Button,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  Menu,
  MenuItem,
  Modal,
  Tabs,
  Tab,
  CircularProgress
} from '@mui/material';
import UploadIcon from '@mui/icons-material/UploadFile';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachFileIcon from '@mui/icons-material/AttachFile';

const EmailEditorPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { emailId } = useParams();
  const [email, setEmail] = useState(null);
  const [previewMode, setPreviewMode] = useState('desktop');
  const [tabIndex, setTabIndex] = useState(0);
  const [chatHistory, setChatHistory] = useState([]);
  const [emailContent, setEmailContent] = useState('');
  const [loadingAI, setLoadingAI] = useState(false);
  const [emailHistory, setEmailHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [imageUploadModalOpen, setImageUploadModalOpen] = useState(false);
  const [uploadedImageUrl, setUploadedImageUrl] = useState('');
  const [imageInsertPrompt, setImageInsertPrompt] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState('');

  useEffect(() => {
    if (state?.email) {
      setEmail(state.email);
      setEmailContent(state.email.customHtml || state.email.content || '');
      setChatHistory([
        { sender: 'ia', text: `Bonjour ! Quel aspect de l'email "${state.email.name}" souhaitez-vous modifier ?` }
      ]);
    } else {
      // Idéalement, ici, on ferait un appel API pour récupérer l'email par son ID
      console.log(`Aucun email trouvé dans le state, charger l'email avec l'ID: ${emailId}`);
      setChatHistory([
        { sender: 'ia', text: `Bonjour ! Prêt à modifier l'email (ID: ${emailId}) ?` }
      ]);
    }
  }, [state, emailId]);
  const [userInput, setUserInput] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importPromptAnchorEl, setImportPromptAnchorEl] = useState(null);
  const [importHtmlError, setImportHtmlError] = useState('');
  const fileInputRef = useRef(null);
  const chatEndRef = useRef(null);

  const handleSend = async () => {
    if (!userInput.trim()) return;
    
    console.log('[EMAIL_EDITOR] === DÉBUT handleSend ===');
    console.log('[EMAIL_EDITOR] User input:', userInput);
    console.log('[EMAIL_EDITOR] Email content actuel (premiers 200 chars):', emailContent.substring(0, 200));
    
    setChatHistory([...chatHistory, { sender: 'user', text: userInput }]);
    const currentInput = userInput;
    setUserInput('');
    setLoadingAI(true);
    
    try {
      // On extrait l'historique des instructions utilisateur
      const instructions = chatHistory.filter(msg => msg.sender === 'user').map(msg => msg.text);
      console.log('[EMAIL_EDITOR] Instructions historiques:', instructions);
      
      const requestData = {
        prompt: currentInput,
        current_html: emailContent,
        instructions
      };
      console.log('[EMAIL_EDITOR] Données envoyées au backend:', requestData);
      
      const res = await api.post('/emails/edit_with_ai', requestData);
      console.log('[EMAIL_EDITOR] Réponse du backend:', res.data);
      
      const data = res.data;
      if (data.success && data.suggestion) {
        console.log('[EMAIL_EDITOR] Succès - HTML suggéré (premiers 200 chars):', data.suggestion.substring(0, 200));
        console.log('[EMAIL_EDITOR] HTML a-t-il changé?', data.suggestion !== emailContent);
        
        // Vérifier si le HTML a réellement changé
        if (data.suggestion !== emailContent) {
          setChatHistory(prev => [...prev, { sender: 'ia', text: 'Voici la version modifiée ! (prévisualisation à jour)' }]);
        setEmailHistory(prev => [...prev, emailContent]); // Ajoute l'ancienne version à l'historique
        setEmailContent(data.suggestion);
          console.log('[EMAIL_EDITOR] Email content mis à jour');
        } else {
          setChatHistory(prev => [...prev, { sender: 'ia', text: 'Limite de quota API atteinte. Votre quota d\'utilisation de l\'API Google Gemini a été dépassé. Veuillez vérifier votre plan et vos détails de facturation, ou réessayer plus tard.' }]);
          console.log('[EMAIL_EDITOR] HTML identique - probablement quota dépassé');
        }
      } else {
        console.log('[EMAIL_EDITOR] Échec - Message:', data.message);
        setChatHistory(prev => [...prev, { sender: 'ia', text: "Erreur lors de la génération IA : " + (data.message || 'inconnue') }]);
      }
    } catch (e) {
      console.error('[EMAIL_EDITOR] Erreur lors de l\'appel API:', e);
      setChatHistory(prev => [...prev, { sender: 'ia', text: "Erreur réseau ou serveur IA." }]);
    }
    
    setLoadingAI(false);
    console.log('[EMAIL_EDITOR] === FIN handleSend ===');
  };

  const handleSave = async () => {
    if (!email || !email.id) return;
    setSaving(true);
    setSaveMessage('');
    try {
      const res = await api.post('/emails/update', {
        email_id: email.id,
        email_config: {
          ...email,
          customHtml: emailContent,
        },
      });
      const data = res.data;
      if (data.success !== false && !data.error) {
        setSaveMessage('Sauvegarde réussie dans Mautic !');
      } else {
        setSaveMessage('Erreur lors de la sauvegarde : ' + (data.message || 'inconnue'));
      }
    } catch (e) {
      setSaveMessage('Erreur réseau lors de la sauvegarde.');
    }
    setSaving(false);
  };

  const openMenu = (e) => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  const openImportModal = (tab) => {
    setTabIndex(tab);
    setImportModalOpen(true);
    closeMenu();
  };

  // Handler pour ouvrir le menu d'import depuis la barre de prompt
  const openPromptImportMenu = (e) => setImportPromptAnchorEl(e.currentTarget);
  const closePromptImportMenu = () => setImportPromptAnchorEl(null);

  // Handler pour sélectionner un fichier HTML
  const handleImportHtmlFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.html')) {
      setImportHtmlError('Veuillez sélectionner un fichier .html');
      return;
    }
    const reader = new FileReader();
    reader.onload = (evt) => {
      setEmailContent(evt.target.result);
      setImportHtmlError('');
      setChatHistory(prev => [...prev, { sender: 'ia', text: 'Le contenu HTML importé a remplacé l’email.' }]);
    };
    reader.onerror = () => {
      setImportHtmlError('Erreur lors de la lecture du fichier HTML.');
    };
    reader.readAsText(file);
    closePromptImportMenu();
    // Reset input value to allow re-import of the same file
    e.target.value = '';
  };

  // Handler pour sélectionner une image
  const handleImportImageFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setImageUploadError('Veuillez sélectionner un fichier image.');
      return;
    }
    setImageUploading(true);
    setImageUploadError('');
    // Upload vers le backend
    const formData = new FormData();
    formData.append('file', file);
    api.post('/emails/upload_image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
      .then(res => {
        const data = res.data;
        if (data.success && data.url) {
          setUploadedImageUrl(data.url);
          setImageUploadModalOpen(true);
        } else {
          setImageUploadError(data.message || 'Erreur lors de l’upload de l’image.');
        }
      })
      .catch(() => setImageUploadError('Erreur réseau lors de l’upload.'))
      .finally(() => setImageUploading(false));
    closePromptImportMenu();
    e.target.value = '';
  };

  // Handler pour insérer l'image via l'IA
  const handleInsertImageWithPrompt = async () => {
    if (!imageInsertPrompt.trim() || !uploadedImageUrl) return;
    setLoadingAI(true);
    setImageUploadModalOpen(false);
    try {
      const res = await api.post('/emails/edit_with_ai', {

          prompt: imageInsertPrompt + ` (insérer l’image suivante : ${uploadedImageUrl})`,
          current_html: emailContent,
          instructions: chatHistory.filter(msg => msg.sender === 'user').map(msg => msg.text),
          image_url: uploadedImageUrl,
      });
      const data = res.data;
      if (data.success && data.suggestion) {
        setChatHistory(prev => [...prev, { sender: 'ia', text: 'Image insérée dans l’email ! (prévisualisation à jour)' }]);
        setEmailHistory(prev => [...prev, emailContent]);
        setEmailContent(data.suggestion);
      } else {
        setChatHistory(prev => [...prev, { sender: 'ia', text: "Erreur IA lors de l’insertion de l’image : " + (data.message || 'inconnue') }]);
      }
    } catch (e) {
      setChatHistory(prev => [...prev, { sender: 'ia', text: "Erreur réseau ou serveur IA lors de l’insertion de l’image." }]);
    }
    setLoadingAI(false);
    setUploadedImageUrl('');
    setImageInsertPrompt('');
  };

  // Fonction utilitaire pour forcer target="_blank" sur tous les liens de l'aperçu
  function addTargetBlankToLinks(html) {
    if (!html) return html;
    // Utilise DOMParser pour manipuler le HTML en toute sécurité
    try {
      const parser = new window.DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const links = doc.querySelectorAll('a[href]');
      links.forEach(link => {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
      });
      return doc.documentElement.innerHTML.replace('<html><head></head><body>', '').replace('</body></html>', '');
    } catch (e) {
      return html; // fallback
    }
  }

  return (
    <Box display="flex" flexDirection="column" height="100vh">
      <Box display="flex" justifyContent="space-between" alignItems="center" p={2} bgcolor="#2c2c2c" color="white">
        <Box display="flex" alignItems="center">
          <Tooltip title="Retour">
            <IconButton onClick={() => navigate(-1)} sx={{ color: 'white', mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h6" display="flex" alignItems="center" gap={1}>
            Édition Email : <span style={{ color: '#007bff' }}>{email?.name || 'Chargement...'}</span>
          </Typography>
        </Box>
        <Box>
          <Tooltip title="Importer">
            <IconButton
              onClick={openMenu}
              sx={{ border: '1px solid #6f42c1', bgcolor: '#6f42c1', color: 'white', m: 0.5 }}
            >
              <UploadIcon />
            </IconButton>
          </Tooltip>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
            <MenuItem onClick={() => openImportModal(0)}>📁 Depuis mon ordinateur</MenuItem>
            <MenuItem onClick={() => openImportModal(1)}>🌐 Depuis une URL</MenuItem>
            <MenuItem onClick={() => openImportModal(2)}>📚 Depuis la bibliothèque</MenuItem>
          </Menu>

          <Tooltip title="Sauvegarder le Brouillon">
            <IconButton 
              sx={{ border: '1px solid #007bff', bgcolor: '#007bff', color: 'white', m: 0.5 }}
              onClick={handleSave}
              disabled={saving}
            >
              <SaveIcon />
            </IconButton>
          </Tooltip>
          {saveMessage && (
            <Typography variant="caption" color={saveMessage.includes('réussie') ? 'success.main' : 'error.main'} sx={{ ml: 2 }}>
              {saveMessage}
            </Typography>
          )}
          <Tooltip title="Soumettre pour Approbation">
            <IconButton sx={{ border: '1px solid #28a745', bgcolor: '#28a745', color: 'white', m: 0.5 }}>
              <SendIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box display="flex" flexGrow={1}>
        {/* Chat Panel */}
        <Box width={{ xs: '100%', md: '35%' }} display="flex" flexDirection="column" bgcolor="#fff" borderRight="1px solid #ddd">
          <Box p={2} flexGrow={1} overflow="auto">
            {chatHistory.map((msg, idx) => (
              <Box key={idx} textAlign={msg.sender === 'user' ? 'right' : 'left'} mb={1}>
                <Box
                  component="span"
                  bgcolor={msg.sender === 'user' ? '#007bff' : '#e9ecef'}
                  color={msg.sender === 'user' ? 'white' : '#333'}
                  px={2} py={1}
                  borderRadius={3}
                  display="inline-block"
                  maxWidth="85%"
                >
                  {msg.text}
                </Box>
              </Box>
            ))}
            <div ref={chatEndRef} />
          </Box>
          <Box display="flex" alignItems="flex-end" p={2} borderTop="1px solid #eee">
            {/* Icône d'import à droite de la barre de prompt */}
            <Tooltip title="Importer un fichier">
              <span>
                <IconButton
                  onClick={openPromptImportMenu}
                  sx={{ mr: 1, borderRadius: '50%', border: '2px solid #6f42c1', color: '#6f42c1', bgcolor: 'white', '&:hover': { bgcolor: '#f3eaff' } }}
                >
                  <AttachFileIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Menu anchorEl={importPromptAnchorEl} open={Boolean(importPromptAnchorEl)} onClose={closePromptImportMenu}>
              <MenuItem onClick={() => { fileInputRef.current.setAttribute('accept', '.html'); fileInputRef.current.click(); }}>Importer un fichier HTML</MenuItem>
              <MenuItem onClick={() => { fileInputRef.current.setAttribute('accept', 'image/*'); fileInputRef.current.click(); }}>Importer une image</MenuItem>
            </Menu>
            {/* Input caché pour l'import de fichier HTML ou image */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".html, image/*"
              style={{ display: 'none' }}
              onChange={(e) => {
                // On choisit le handler selon le type de fichier
                const file = e.target.files && e.target.files[0];
                if (!file) return;
                if (file.name.endsWith('.html')) {
                  handleImportHtmlFile(e);
                  fileInputRef.current.setAttribute('accept', '.html, image/*');
                } else if (file.type.startsWith('image/')) {
                  handleImportImageFile(e);
                  fileInputRef.current.setAttribute('accept', '.html, image/*');
                }
              }}
            />
            {/* Affichage d'une erreur d'import HTML ou image si besoin */}
            {(importHtmlError || imageUploadError) && (
              <Typography variant="caption" color="error" sx={{ ml: 2 }}>{importHtmlError || imageUploadError}</Typography>
            )}
            {/* Champ de saisie du prompt */}
            <TextField
              fullWidth
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Décrivez votre modification... (Maj+Entrée pour une nouvelle ligne)"
              multiline
              maxRows={5}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '20px',
                  padding: '10px 15px',
                  '& fieldset': {
                    borderColor: '#ccc',
                  },
                  '&:hover fieldset': {
                    borderColor: '#888',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
              disabled={loadingAI || imageUploading}
            />
            {/* Affichage du chargement lors de l'upload image */}
            {imageUploading && (
              <Box display="flex" alignItems="center" ml={2}>
                <CircularProgress size={24} />
                <Typography variant="caption" sx={{ ml: 1 }}>Upload en cours...</Typography>
              </Box>
            )}
            <Tooltip title="Envoyer">
              <span>
                <IconButton
                  onClick={handleSend}
                  disabled={!userInput.trim() || loadingAI}
                  sx={{
                    ml: 1,
                    borderRadius: '50%',
                    border: '2px solid',
                    borderColor: 'primary.main',
                    color: 'white',
                    bgcolor: 'primary.main',
                    '&:hover': { bgcolor: 'primary.dark' },
                    '&.Mui-disabled': {
                      bgcolor: '#ccc',
                      color: '#fff',
                      borderColor: '#ccc',
                    }
                  }}
                >
                  <ArrowUpwardIcon />
                </IconButton>
              </span>
            </Tooltip>
            {loadingAI && <CircularProgress size={24} sx={{ ml: 2 }} />}
          </Box>
        </Box>

        {/* Preview Panel */}
        <Box flexGrow={1} p={3} bgcolor="#e9ecef">
          <Box textAlign="center" mb={2}>
            <Tooltip title="Vue Desktop">
              <IconButton
                onClick={() => setPreviewMode('desktop')}
                sx={{ m: 1, border: '1px solid #007bff', bgcolor: previewMode === 'desktop' ? '#007bff' : 'transparent', color: previewMode === 'desktop' ? 'white' : '#007bff' }}
              >
                <DesktopWindowsIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Vue Mobile">
              <IconButton
                onClick={() => setPreviewMode('mobile')}
                sx={{ m: 1, border: '1px solid #007bff', bgcolor: previewMode === 'mobile' ? '#007bff' : 'transparent', color: previewMode === 'mobile' ? 'white' : '#007bff' }}
              >
                <SmartphoneIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Box
            mx="auto"
            width={previewMode === 'desktop' ? '100%' : 375}
            maxWidth={800} // Limite la largeur max en mode desktop pour la lisibilité
            bgcolor="white"
            borderRadius={2}
            boxShadow={3}
            height={600} // Hauteur fixe pour le conteneur de l'iframe
            sx={{ overflow: 'hidden' }} // Cache tout ce qui dépasse
          >
            <iframe
              title="Email Preview"
              srcDoc={addTargetBlankToLinks(emailContent) || '<p>Contenu de l\'email non disponible.</p>'}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Import Modal */}
      <Modal open={importModalOpen} onClose={() => setImportModalOpen(false)}>
        <Box
          bgcolor="white"
          p={4}
          borderRadius={2}
          maxWidth={600}
          mx="auto"
          my={10}
          boxShadow={10}
        >
          <Typography variant="h6" gutterBottom>
            📁 Importer des éléments
          </Typography>
          <Tabs value={tabIndex} onChange={(_, val) => setTabIndex(val)}>
            <Tab label="🖼️ Images" />
            <Tab label="🌐 URL" />
            <Tab label="📚 Bibliothèque" />
          </Tabs>
          <Box mt={2}>
            {tabIndex === 0 && <Typography>Zone d'import d'images (à implémenter)</Typography>}
            {tabIndex === 1 && <Typography>Import via URL (à implémenter)</Typography>}
            {tabIndex === 2 && <Typography>Bibliothèque d'images (à implémenter)</Typography>}
          </Box>
          <Box mt={3} textAlign="right">
            <Button onClick={() => setImportModalOpen(false)} variant="contained" color="inherit">
              Fermer
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Modal pour prompt d'insertion d'image */}
      <Modal open={imageUploadModalOpen} onClose={() => setImageUploadModalOpen(false)}>
        <Box bgcolor="white" p={4} borderRadius={2} maxWidth={500} mx="auto" my={10} boxShadow={10}>
          <Typography variant="h6" gutterBottom>Où insérer l’image ?</Typography>
          <img src={uploadedImageUrl} alt="Aperçu" style={{ maxWidth: '100%', maxHeight: 200, marginBottom: 16 }} />
          <TextField
            fullWidth
            label="Décrivez où insérer l’image dans l’email (ex: après le titre, remplacer la bannière, etc.)"
            value={imageInsertPrompt}
            onChange={e => setImageInsertPrompt(e.target.value)}
            multiline
            minRows={2}
            sx={{ mb: 2 }}
          />
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button onClick={() => setImageUploadModalOpen(false)} color="inherit">Annuler</Button>
            <Button onClick={handleInsertImageWithPrompt} variant="contained" disabled={!imageInsertPrompt.trim() || loadingAI}>
              Insérer l’image
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default EmailEditorPage;
